import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { TicketTypeQuickEdit } from "../components/ticketing/TicketTypeQuickEdit";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { PageHeader } from "../components/ui/kaizen";

const TYPE_CODE_COLORS = {
  ga: "border-zinc-700 text-zinc-300",
  reserved: "border-amber-500/40 text-amber-300",
  vip_table: "border-purple-500/40 text-purple-300",
};

export default function TicketingModule() {
  const [events, setEvents] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [orders, setOrders] = useState([]);
  const [seatmapInfo, setSeatmapInfo] = useState(null);
  const [sections, setSections] = useState([]);

  const [createEventOpen, setCreateEventOpen] = useState(false);
  const [eventForm, setEventForm] = useState({
    title_fr: "",
    title_en: "",
    description_fr: "",
    description_en: "",
    date: "",
    time: "13:00",
    venue_name: "Institut Impact Élite Martial",
    venue_address: "1218 7e Rue",
    venue_city: "Val-d'Or",
    is_published: false,
  });

  const [typeOpen, setTypeOpen] = useState(false);
  const [typeForm, setTypeForm] = useState({
    code: "ga",
    name_fr: "",
    name_en: "",
    description_fr: "",
    description_en: "",
    price_cad: 20,
    capacity: 100,
    seats_per_unit: 1,
    sort_order: 0,
    is_active: true,
  });

  const loadEvents = async () => {
    try {
      const r = await api.get("/ticketing/events");
      setEvents(r.data);
      if (r.data.length && !selectedId) setSelectedId(r.data[0].id);
    } catch {
      toast.error("Erreur chargement événements");
    }
  };

  const loadDetail = async (eid) => {
    if (!eid) return;
    try {
      const [d, dash, o, sm] = await Promise.all([
        api.get(`/ticketing/events/${eid}`),
        api.get(`/ticketing/events/${eid}/dashboard`),
        api.get(`/ticketing/events/${eid}/orders`),
        api.get(`/ticketing/events/${eid}/seatmap`).catch(() => ({ data: { seatmap: null } })),
      ]);
      setSelectedDetail(d.data);
      setDashboard(dash.data);
      setOrders(o.data);
      setSeatmapInfo(sm.data.seatmap);
    } catch {
      toast.error("Erreur chargement détail événement");
    }
  };

  const addSection = () => setSections([...sections, { name: "", type: "standard", ticket_type_id: "", rows: 3, seats_per_row: 10 }]);
  const updateSection = (i, patch) => setSections(sections.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const removeSection = (i) => setSections(sections.filter((_, idx) => idx !== i));

  const saveSeatmap = async () => {
    const valid = sections.filter((s) => s.name && s.ticket_type_id && s.rows > 0 && s.seats_per_row > 0);
    if (valid.length === 0) { toast.error("Ajoute au moins une section valide (nom + type de billet)"); return; }
    try {
      const r = await api.put(`/ticketing/events/${selectedId}/seatmap`, { sections: valid });
      toast.success(`✅ Plan généré : ${r.data.total_seats} sièges, ${r.data.sections} sections`);
      setSections([]);
      await loadDetail(selectedId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  useEffect(() => { loadEvents(); }, []);
  useEffect(() => { if (selectedId) loadDetail(selectedId); }, [selectedId]);

  const submitEvent = async () => {
    if (!eventForm.title_fr || !eventForm.title_en || !eventForm.date) {
      toast.error("Titre FR/EN + date obligatoires");
      return;
    }
    try {
      const r = await api.post("/ticketing/events", eventForm);
      toast.success(`✅ ${r.data.title_fr} créé`);
      setCreateEventOpen(false);
      setEventForm({ ...eventForm, title_fr: "", title_en: "", description_fr: "", description_en: "", date: "" });
      await loadEvents();
      setSelectedId(r.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const togglePublish = async (e) => {
    try {
      const r = await api.patch(`/ticketing/events/${e.id}`, { is_published: !e.is_published });
      toast.success(r.data.is_published ? "✅ Publié" : "🔒 Dépublié");
      await loadEvents();
      if (selectedId === e.id) await loadDetail(e.id);
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const submitType = async () => {
    if (!typeForm.name_fr || !typeForm.name_en) {
      toast.error("Nom FR/EN obligatoires");
      return;
    }
    try {
      await api.post(`/ticketing/events/${selectedId}/ticket-types`, typeForm);
      toast.success(`✅ ${typeForm.name_fr} ajouté`);
      setTypeOpen(false);
      setTypeForm({ ...typeForm, name_fr: "", name_en: "", description_fr: "", description_en: "", price_cad: 20, capacity: 100 });
      await loadDetail(selectedId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const deleteType = async (tid, name) => {
    if (!window.confirm(`Supprimer "${name}" ?`)) return;
    try {
      await api.delete(`/ticketing/ticket-types/${tid}`);
      toast.success("Supprimé");
      await loadDetail(selectedId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const deleteEvent = async (eid, name) => {
    if (!window.confirm(`Supprimer "${name}" ?`)) return;
    try {
      await api.delete(`/ticketing/events/${eid}`);
      toast.success("Supprimé");
      setSelectedId(null);
      await loadEvents();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const publicUrl = (slug) => {
    const origin = window.location.origin;
    return `${origin}/billetterie/${slug}`;
  };

  return (
    <div className="space-y-6 p-6 md:p-8" data-testid="ticketing-module">
      {/* Header */}
      <PageHeader
        title="Billetterie événements"
        breadcrumbs={[{ label: "Compétition & événements" }, { label: "Billetterie" }]}
        subtitle="Gala, Mini Compétitions, Démonstrations : crée l'événement, configure les types de billets (GA / Réservé / Table VIP), publie et suis les ventes en temps réel."
        actions={
          <Button onClick={() => setCreateEventOpen(true)} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold" data-testid="create-event-btn">
            + Nouvel événement
          </Button>
        }
      />

      {/* Events list + selected detail */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Events list */}
        <div className="space-y-2" data-testid="events-list">
          <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-2">Événements</div>
          {events.length === 0 ? (
            <div className="text-xs text-zinc-500 italic py-4">Aucun événement. Crée le premier !</div>
          ) : (
            events.map((e) => {
              const isSel = selectedId === e.id;
              return (
                <button
                  key={e.id}
                  onClick={() => setSelectedId(e.id)}
                  className={`w-full text-left border rounded-xl p-4 transition ${isSel ? "border-amber-500/50 bg-amber-500/10" : "border-zinc-800 bg-zinc-950/40 hover:border-zinc-600"}`}
                  data-testid={`event-card-${e.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-sm text-zinc-100 truncate">{e.title_fr}</div>
                    {e.is_published ? (
                      <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 text-[10px]">PUBLIÉ</Badge>
                    ) : (
                      <Badge variant="outline" className="border-zinc-700 text-zinc-500 text-[10px]">BROUILLON</Badge>
                    )}
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-1">📅 {e.date}</div>
                  <div className="text-[10px] text-zinc-400 mt-2">
                    {e.tickets_sold} billets · {(e.revenue_cad || 0).toLocaleString()} $
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Selected event detail */}
        <div className="lg:col-span-2 space-y-4">
          {selectedDetail && dashboard ? (
            <>
              {/* Event header */}
              <div className="border border-zinc-800 rounded-2xl p-5 bg-zinc-950/40">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="text-2xl font-bold">{selectedDetail.event.title_fr}</div>
                    <div className="text-sm text-zinc-400">{selectedDetail.event.title_en}</div>
                    <div className="text-xs text-zinc-500 mt-2">
                      📅 {selectedDetail.event.date} {selectedDetail.event.time} · 📍 {selectedDetail.event.venue_name} ({selectedDetail.event.venue_city})
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2 border border-zinc-800 rounded-lg px-3 py-2">
                      <Switch
                        checked={selectedDetail.event.is_published}
                        onCheckedChange={() => togglePublish(selectedDetail.event)}
                        data-testid="publish-toggle"
                      />
                      <span className="text-xs">{selectedDetail.event.is_published ? "Publié" : "Brouillon"}</span>
                    </div>
                    <button
                      onClick={() => deleteEvent(selectedDetail.event.id, selectedDetail.event.title_fr)}
                      className="text-red-400 hover:text-red-300 text-xs border border-red-500/40 rounded-lg px-3 py-2"
                      data-testid="delete-event-btn"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                {selectedDetail.event.is_published && (
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] tracking-[0.3em] text-amber-400">URL PUBLIQUE :</span>
                    <a
                      href={publicUrl(selectedDetail.event.slug)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-amber-300 hover:text-amber-200 underline truncate max-w-md"
                      data-testid="public-url-link"
                    >
                      {publicUrl(selectedDetail.event.slug)}
                    </a>
                    <button
                      onClick={() => { navigator.clipboard.writeText(publicUrl(selectedDetail.event.slug)); toast.success("URL copiée"); }}
                      className="text-[10px] text-zinc-400 hover:text-white border border-zinc-700 rounded-full px-2 py-0.5"
                    >
                      📋 Copier
                    </button>
                  </div>
                )}
              </div>

              {/* KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dashboard-kpis">
                <KPI label="Revenus" value={`${(dashboard.kpis.revenue_cad || 0).toLocaleString()} $`} accent />
                <KPI label="Billets vendus" value={dashboard.kpis.tickets_sold} />
                <KPI label="Commandes payées" value={dashboard.kpis.orders_paid} />
                <KPI label="Commandes en attente" value={dashboard.kpis.orders_pending} />
              </div>

              {/* Ticket types */}
              <div className="border border-zinc-800 rounded-2xl p-5 bg-zinc-950/40">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] tracking-[0.3em] text-amber-400">TYPES DE BILLETS</div>
                  <Button
                    onClick={() => setTypeOpen(true)}
                    size="sm"
                    className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 text-xs"
                    data-testid="add-ticket-type-btn"
                  >
                    + Ajouter
                  </Button>
                </div>
                {dashboard.by_type.length === 0 ? (
                  <div className="text-xs text-zinc-500 italic py-3">Aucun type de billet. Ajoute Général / Réservé / Table VIP.</div>
                ) : (
                  <div className="space-y-3" data-testid="ticket-types-admin">
                    {dashboard.by_type.map((tt) => (
                      <div key={tt.id} className={`border rounded-xl p-4 ${TYPE_CODE_COLORS[tt.code] || "border-zinc-800"} bg-black/30`}>
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <div className="text-base font-bold">{tt.name_fr}</div>
                              <Badge variant="outline" className="text-[10px]">{tt.code}</Badge>
                            </div>
                            <div className="text-xs opacity-70 mt-0.5">{tt.name_en}</div>
                            <div className="text-xs mt-2">
                              <span className="font-bold text-amber-300">{tt.price_cad.toFixed(2)} $</span>
                              <span className="opacity-60"> · {tt.sold}/{tt.capacity} vendus ({tt.pct_sold}%) · {(tt.revenue_cad || 0).toLocaleString()} $</span>
                            </div>
                            <div className="mt-2 h-1 bg-black/50 rounded-full overflow-hidden">
                              <div
                                className={`h-full ${tt.pct_sold >= 80 ? "bg-emerald-500" : tt.pct_sold >= 30 ? "bg-amber-500" : "bg-zinc-600"}`}
                                style={{ width: `${Math.min(tt.pct_sold, 100)}%` }}
                              />
                            </div>
                          </div>
                          <button
                            onClick={() => deleteType(tt.id, tt.name_fr)}
                            className="text-red-400 hover:text-red-300 text-xs"
                            data-testid={`delete-type-${tt.code}`}
                          >🗑️</button>
                        </div>
                        <TicketTypeQuickEdit tt={tt} onSaved={() => loadDetail(selectedId)} />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* V2 — Plan de salle (sièges visuels) */}
              <div className="border border-zinc-800 rounded-2xl p-5 bg-zinc-950/40" data-testid="seatmap-builder">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div className="text-[10px] tracking-[0.3em] text-amber-400">🎟️ PLAN DE SALLE (V2 — SIÈGES VISUELS)</div>
                  {seatmapInfo && (
                    <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 text-[10px]">
                      ✓ {seatmapInfo.total_seats} sièges · {seatmapInfo.sections.length} sections
                    </Badge>
                  )}
                </div>
                {seatmapInfo && (
                  <div className="mb-3 text-xs text-zinc-400">
                    Plan actif : {seatmapInfo.sections.map((s) => `${s.name} (${s.type})`).join(" · ")}.
                    <span className="text-zinc-500"> Regénère ci-dessous pour le remplacer.</span>
                  </div>
                )}
                {(dashboard.by_type.length === 0) ? (
                  <div className="text-xs text-zinc-500 italic">Crée d'abord tes types de billets (VIP, Régulier…) pour les assigner aux sections.</div>
                ) : (
                  <div className="space-y-3">
                    {sections.map((sec, i) => (
                      <div key={i} className="border border-zinc-800 rounded-lg p-3 bg-black/30 grid grid-cols-2 sm:grid-cols-6 gap-2 items-end" data-testid={`section-row-${i}`}>
                        <div className="col-span-2 sm:col-span-2">
                          <Label className="text-[10px]">Nom section</Label>
                          <Input value={sec.name} onChange={(e) => updateSection(i, { name: e.target.value })} placeholder="VIP Avant" className="bg-black border-zinc-800 h-8 text-sm" />
                        </div>
                        <div>
                          <Label className="text-[10px]">Type</Label>
                          <select value={sec.type} onChange={(e) => updateSection(i, { type: e.target.value })} className="w-full bg-black border border-zinc-800 rounded h-8 text-sm px-1">
                            <option value="standard">Standard</option>
                            <option value="vip">VIP</option>
                          </select>
                        </div>
                        <div>
                          <Label className="text-[10px]">Billet</Label>
                          <select value={sec.ticket_type_id} onChange={(e) => updateSection(i, { ticket_type_id: e.target.value })} className="w-full bg-black border border-zinc-800 rounded h-8 text-sm px-1" data-testid={`section-ticket-${i}`}>
                            <option value="">—</option>
                            {dashboard.by_type.map((tt) => <option key={tt.id} value={tt.id}>{tt.name_fr}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label className="text-[10px]">Rangées</Label>
                          <Input type="number" min={1} max={50} value={sec.rows} onChange={(e) => updateSection(i, { rows: parseInt(e.target.value) || 1 })} className="bg-black border-zinc-800 h-8 text-sm" />
                        </div>
                        <div className="flex gap-1 items-end">
                          <div className="flex-1">
                            <Label className="text-[10px]">Sièges/rang</Label>
                            <Input type="number" min={1} max={80} value={sec.seats_per_row} onChange={(e) => updateSection(i, { seats_per_row: parseInt(e.target.value) || 1 })} className="bg-black border-zinc-800 h-8 text-sm" />
                          </div>
                          <button onClick={() => removeSection(i)} className="text-red-400 hover:text-red-300 text-sm pb-1.5">🗑️</button>
                        </div>
                      </div>
                    ))}
                    <div className="flex gap-2 flex-wrap">
                      <Button onClick={addSection} variant="outline" size="sm" className="text-xs border-zinc-700" data-testid="add-section-btn">+ Ajouter une section</Button>
                      {sections.length > 0 && (
                        <Button onClick={saveSeatmap} size="sm" className="text-xs bg-amber-500 hover:bg-amber-400 text-black font-bold" data-testid="generate-seatmap-btn">⚡ Générer le plan de salle</Button>
                      )}
                    </div>
                    <p className="text-[10px] text-zinc-500">Le plan visuel apparaîtra sur la page publique d'achat. Les clients cliqueront leurs sièges directement.</p>
                  </div>
                )}
              </div>
              <div className="border border-zinc-800 rounded-2xl p-5 bg-zinc-950/40">
                <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-3">COMMANDES RÉCENTES</div>
                {orders.length === 0 ? (
                  <div className="text-xs text-zinc-500 italic py-3">Aucune commande pour cet événement.</div>
                ) : (
                  <div className="space-y-2" data-testid="orders-list">
                    {orders.slice(0, 20).map((o) => (
                      <div key={o.id} className="border border-zinc-900 rounded-lg p-3 bg-black/30 flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold truncate">{o.customer_name}</div>
                          <div className="text-[10px] text-zinc-500">{o.customer_email} · {o.items_count} billet(s) · {new Date(o.created_at).toLocaleString("fr-CA")}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={o.payment_status === "paid" ? "border-emerald-500/40 text-emerald-300 text-[10px]" : "border-amber-500/40 text-amber-300 text-[10px]"}>
                            {o.payment_status}
                          </Badge>
                          <span className="text-sm font-bold text-amber-300">{o.total_cad.toFixed(2)} $</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="border border-zinc-800 rounded-2xl p-12 text-center bg-zinc-950/30">
              <div className="text-zinc-500 text-sm">Sélectionne un événement (ou crée-en un)</div>
            </div>
          )}
        </div>
      </div>

      {/* Create event dialog */}
      <Dialog open={createEventOpen} onOpenChange={setCreateEventOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl" data-testid="create-event-dialog">
          <DialogHeader><DialogTitle>Nouvel événement billetterie</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Titre FR *</Label><Input value={eventForm.title_fr} onChange={(e) => setEventForm({ ...eventForm, title_fr: e.target.value })} placeholder="Mini Comp KAIZEN — Automne 2026" className="bg-black border-zinc-800" data-testid="event-title-fr" /></div>
              <div><Label className="text-xs">Titre EN *</Label><Input value={eventForm.title_en} onChange={(e) => setEventForm({ ...eventForm, title_en: e.target.value })} placeholder="KAIZEN Mini Tournament — Fall 2026" className="bg-black border-zinc-800" data-testid="event-title-en" /></div>
            </div>
            <div><Label className="text-xs">Description FR</Label><Textarea value={eventForm.description_fr} onChange={(e) => setEventForm({ ...eventForm, description_fr: e.target.value })} className="bg-black border-zinc-800 min-h-[70px]" /></div>
            <div><Label className="text-xs">Description EN</Label><Textarea value={eventForm.description_en} onChange={(e) => setEventForm({ ...eventForm, description_en: e.target.value })} className="bg-black border-zinc-800 min-h-[70px]" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Date *</Label><Input type="date" value={eventForm.date} onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })} className="bg-black border-zinc-800" data-testid="event-date" /></div>
              <div><Label className="text-xs">Heure</Label><Input value={eventForm.time} onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })} className="bg-black border-zinc-800" /></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2"><Label className="text-xs">Lieu (nom)</Label><Input value={eventForm.venue_name} onChange={(e) => setEventForm({ ...eventForm, venue_name: e.target.value })} className="bg-black border-zinc-800" /></div>
              <div><Label className="text-xs">Ville</Label><Input value={eventForm.venue_city} onChange={(e) => setEventForm({ ...eventForm, venue_city: e.target.value })} className="bg-black border-zinc-800" /></div>
            </div>
            <div className="flex items-center gap-2 border border-zinc-800 rounded-lg px-3 py-2">
              <Switch checked={eventForm.is_published} onCheckedChange={(v) => setEventForm({ ...eventForm, is_published: v })} />
              <span className="text-xs">Publier immédiatement (URL publique active)</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateEventOpen(false)}>Annuler</Button>
            <Button onClick={submitEvent} className="bg-amber-500 hover:bg-amber-400 text-black" data-testid="submit-event">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add ticket type dialog */}
      <Dialog open={typeOpen} onOpenChange={setTypeOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl" data-testid="create-type-dialog">
          <DialogHeader><DialogTitle>Nouveau type de billet</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
            <div>
              <Label className="text-xs">Type</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {[
                  { v: "ga", l: "🎟️ Admission générale", suggest: { name_fr: "Admission générale", name_en: "General Admission", seats_per_unit: 1, price_cad: 20 } },
                  { v: "reserved", l: "🪑 Place réservée", suggest: { name_fr: "Place réservée", name_en: "Reserved Seat", seats_per_unit: 1, price_cad: 40 } },
                  { v: "vip_table", l: "👑 Table VIP", suggest: { name_fr: "Table VIP (8 places)", name_en: "VIP Table (8 seats)", seats_per_unit: 8, price_cad: 400 } },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    onClick={() => setTypeForm({ ...typeForm, code: opt.v, ...opt.suggest })}
                    className={`text-left border rounded-lg p-2 text-xs ${typeForm.code === opt.v ? "border-amber-500/60 bg-amber-500/10" : "border-zinc-800 bg-black/30"}`}
                    data-testid={`type-code-${opt.v}`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Nom FR *</Label><Input value={typeForm.name_fr} onChange={(e) => setTypeForm({ ...typeForm, name_fr: e.target.value })} className="bg-black border-zinc-800" data-testid="type-name-fr" /></div>
              <div><Label className="text-xs">Nom EN *</Label><Input value={typeForm.name_en} onChange={(e) => setTypeForm({ ...typeForm, name_en: e.target.value })} className="bg-black border-zinc-800" data-testid="type-name-en" /></div>
            </div>
            <div><Label className="text-xs">Description FR</Label><Textarea value={typeForm.description_fr} onChange={(e) => setTypeForm({ ...typeForm, description_fr: e.target.value })} className="bg-black border-zinc-800 min-h-[50px]" /></div>
            <div><Label className="text-xs">Description EN</Label><Textarea value={typeForm.description_en} onChange={(e) => setTypeForm({ ...typeForm, description_en: e.target.value })} className="bg-black border-zinc-800 min-h-[50px]" /></div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-xs">Prix CAD</Label><Input type="number" value={typeForm.price_cad} onChange={(e) => setTypeForm({ ...typeForm, price_cad: Number(e.target.value) })} className="bg-black border-zinc-800" data-testid="type-price" /></div>
              <div><Label className="text-xs">Capacité</Label><Input type="number" value={typeForm.capacity} onChange={(e) => setTypeForm({ ...typeForm, capacity: Number(e.target.value) })} className="bg-black border-zinc-800" data-testid="type-capacity" /></div>
              <div><Label className="text-xs">Places/unité</Label><Input type="number" value={typeForm.seats_per_unit} onChange={(e) => setTypeForm({ ...typeForm, seats_per_unit: Number(e.target.value) })} className="bg-black border-zinc-800" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTypeOpen(false)}>Annuler</Button>
            <Button onClick={submitType} className="bg-amber-500 hover:bg-amber-400 text-black" data-testid="submit-type">Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPI({ label, value, accent }) {
  return (
    <div className={`rounded-xl border p-3 ${accent ? "border-amber-500/40 bg-amber-500/5" : "border-zinc-800 bg-zinc-950/50"}`}>
      <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${accent ? "text-amber-300" : "text-zinc-100"}`}>{value}</div>
    </div>
  );
}
